import React, { useEffect, useState } from "react";
import AttachMoney from "@mui/icons-material/AttachMoney";
import CalendarMonth from "@mui/icons-material/CalendarMonth";
import Flight from "@mui/icons-material/Flight";
import Hotel from "@mui/icons-material/Hotel";
import Star from "@mui/icons-material/Star";
import { formatChatCardStayLineHe } from "../../../utils/chatCardStayLineHe";
import {
  comparableChatHotelName,
  normalizeCustomerChatHotelCard,
  resolveChatCardStarCount,
} from "../../../utils/customerChatHotelCardNormalize";

const inlineIconSx = (size, color) => ({
  fontSize: size,
  color: color || "inherit",
  flexShrink: 0,
  marginInlineEnd: "5px",
  verticalAlign: "middle",
});

const PLACEHOLDER_IMG =
  "data:image/svg+xml," +
  encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' width='320' height='200' viewBox='0 0 320 200'>" +
      "<rect fill='#e9ecef' width='320' height='200'/>" +
      "<text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='#adb5bd' " +
      "font-family='system-ui,sans-serif' font-size='14'>מלון</text></svg>"
  );

function SafeImg({ src, alt, className }) {
  const [broken, setBroken] = useState(false);
  const effective = broken || !src ? PLACEHOLDER_IMG : src;
  return (
    <img
      className={className}
      src={effective}
      alt={alt || ""}
      loading="lazy"
      decoding="async"
      onError={() => setBroken(true)}
    />
  );
}

function starRatingCount(n) {
  if (n == null || Number.isNaN(Number(n))) return null;
  const v = Math.min(5, Math.max(0, Math.round(Number(n))));
  if (v <= 0) return null;
  return v;
}

function formatStayLine(card) {
  const withDow = formatChatCardStayLineHe(card);
  if (withDow) return withDow;
  const summary =
    typeof card?.stay_summary_he === "string" && card.stay_summary_he.trim()
      ? card.stay_summary_he.trim()
      : "";
  if (summary) return summary;
  return null;
}

function ChatSheet({ open, title, onClose, children }) {
  if (!open) return null;
  return (
    <div className="hr-cvac-sheet" role="dialog" aria-modal="true" aria-label={title}>
      <button type="button" className="hr-cvac-sheet__backdrop" onClick={onClose} aria-label="סגור" />
      <div className="hr-cvac-sheet__panel">
        <div className="hr-cvac-sheet__toolbar">
          <h4 className="hr-cvac-sheet__title">{title}</h4>
          <button type="button" className="hr-cvac-sheet__x" onClick={onClose} aria-label="סגור">
            ×
          </button>
        </div>
        <div className="hr-cvac-sheet__body">{children}</div>
      </div>
    </div>
  );
}

/**
 * Full hotel shortlist card for customer vacation chat (structured API fields).
 */
export default function HotelShortlistCard({
  card,
  disabled,
  onSelectOffer,
  onViewPackageDetails,
  onPickAlternateDeal,
}) {
  const { mainImage, thumbImages } = normalizeCustomerChatHotelCard(card);
  const rank = card?.offer_rank ?? card?.offerRank;
  const hotelName =
    (typeof card?.hotel_name === "string" && card.hotel_name.trim()) ||
    (typeof card?.display_title === "string" && card.display_title.trim()) ||
    "מלון";
  const rawTitle = typeof card?.title === "string" ? card.title.trim() : "";
  const labelHe =
    typeof card?.offer_label_he === "string" ? card.offer_label_he.trim() : "";
  const hotelKey = comparableChatHotelName(hotelName);
  const pickOfferBadge = () => {
    const candidates = [labelHe, rawTitle].filter(Boolean);
    for (const c of candidates) {
      if (comparableChatHotelName(c) !== hotelKey) return c;
    }
    return "";
  };
  const offerBadge = pickOfferBadge();
  const locationLine =
    (typeof card?.location_label === "string" && card.location_label.trim()) ||
    (typeof card?.area_line === "string" && card.area_line.trim()) ||
    (typeof card?.destination_code === "string" && card.destination_code.trim()) ||
    (typeof card?.destination === "string" && card.destination.trim()) ||
    "";
  const aboutShort = typeof card?.about_short === "string" ? card.about_short.trim() : "";
  const aboutFullRaw = typeof card?.about_full === "string" ? card.about_full.trim() : "";
  const aboutFull = aboutFullRaw || aboutShort;
  const hasAbout = Boolean(aboutFull);
  const alternates = Array.isArray(card?.chat_alternate_offers) ? card.chat_alternate_offers : [];
  const hasAlternates = alternates.length > 0;
  const starsCount = starRatingCount(resolveChatCardStarCount(card));
  const priceAmount =
    card?.price_ils != null && !Number.isNaN(Number(card.price_ils)) ? Number(card.price_ils) : null;
  const priceFormatted =
    priceAmount != null ? `${Math.round(priceAmount).toLocaleString("he-IL")} ₪` : null;

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const b2c = card?.b2c_package_view;
    if (!b2c || typeof b2c !== "object") return;
    const vac = b2c.vacation_results_display_price_ils;
    if (vac != null && priceAmount != null && Math.abs(Number(vac) - Number(priceAmount)) > 0.51) {
      // eslint-disable-next-line no-console -- B2C mirror audit
      console.error("[CVAC-B2C] package card price_ils out of sync with b2c_package_view", {
        hotel: hotelName,
        price_ils: priceAmount,
        vacation_results_display_price_ils: vac,
      });
    }
  }, [card?.b2c_package_view, priceAmount, hotelName]);

  const [aboutOpen, setAboutOpen] = useState(false);
  const [altOpen, setAltOpen] = useState(false);

  const facilities = Array.isArray(card?.facilities) ? card.facilities.filter(Boolean).slice(0, 8) : [];
  const reasonHe = typeof card?.reason_he === "string" ? card.reason_he.trim() : "";
  const showReason =
    reasonHe &&
    comparableChatHotelName(reasonHe) !== hotelKey &&
    reasonHe !== aboutShort &&
    reasonHe !== offerBadge;
  const stayLine = formatStayLine(card);
  const airlineLine =
    typeof card?.chat_airline_he === "string" && card.chat_airline_he.trim()
      ? `חברת תעופה: ${card.chat_airline_he.trim()}`
      : null;
  const routeLine =
    typeof card?.chat_flight_route_he === "string" && card.chat_flight_route_he.trim()
      ? card.chat_flight_route_he.trim()
      : null;
  const flightTimesLine =
    typeof card?.chat_flight_times_he === "string" && card.chat_flight_times_he.trim()
      ? card.chat_flight_times_he.trim()
      : null;

  return (
    <article className="hr-cvac-hcard">
      <div className="hr-cvac-hcard__hero-wrap hr-cvac-hcard__hero-wrap--static">
        <SafeImg src={mainImage} className="hr-cvac-hcard__hero" alt={hotelName} />
        {starsCount != null ? (
          <div
            className="hr-cvac-hcard__hero-stars"
            aria-label={`דירוג מלון: ${starsCount} כוכבים`}
          >
            <span className="hr-cvac-hcard__hero-stars-row" aria-hidden>
              {Array.from({ length: starsCount }, (_, i) => (
                <Star key={i} className="hr-cvac-hcard__hero-star-icon" fontSize="inherit" />
              ))}
            </span>
          </div>
        ) : null}
      </div>
      {thumbImages.length ? (
        <div className="hr-cvac-hcard__thumbs">
          {thumbImages.map((u, idx) => (
            <div key={`${u}-${idx}`} className="hr-cvac-hcard__thumb-cell">
              <SafeImg src={u} className="hr-cvac-hcard__thumb" alt="" />
            </div>
          ))}
        </div>
      ) : null}
      <div className="hr-cvac-hcard__body">
        <div className="hr-cvac-hcard__headline">
          <Hotel sx={inlineIconSx(18, "#0b2239")} aria-hidden />
          <h3 className="hr-cvac-hcard__title">{hotelName}</h3>
          {offerBadge ? <span className="hr-cvac-hcard__badge">{offerBadge}</span> : null}
        </div>
        {stayLine ? (
          <p className="hr-cvac-hcard__stays hr-cvac-hcard__row-icon">
            <CalendarMonth sx={inlineIconSx(16, "#1e3a4c")} aria-hidden />
            <span>{stayLine}</span>
          </p>
        ) : null}
        {airlineLine ? (
          <p className="hr-cvac-hcard__flight-meta hr-cvac-hcard__row-icon">
            <Flight sx={inlineIconSx(16, "#495057")} aria-hidden />
            <span>{airlineLine}</span>
          </p>
        ) : null}
        {routeLine ? <p className="hr-cvac-hcard__flight-meta">{routeLine}</p> : null}
        {flightTimesLine ? <p className="hr-cvac-hcard__flight-meta">{flightTimesLine}</p> : null}
        {locationLine ? <p className="hr-cvac-hcard__area">{locationLine}</p> : null}
        <p className="hr-cvac-hcard__price hr-cvac-hcard__row-icon">
          <AttachMoney sx={inlineIconSx(17, "#0d6efd")} aria-hidden />
          <span>
            {priceFormatted != null
              ? `מחיר: ${priceFormatted}`
              : "טוען מחיר…"}
          </span>
        </p>
        {aboutShort ? <p className="hr-cvac-hcard__about-short">{aboutShort}</p> : null}
        {showReason ? <p className="hr-cvac-hcard__reason">{reasonHe}</p> : null}

        <div className="hr-cvac-hcard__actions">
          <button
            type="button"
            className="hr-cvac-hcard__btn hr-cvac-hcard__btn--primary"
            disabled={disabled || rank == null}
            onClick={() => rank != null && onSelectOffer?.(rank, card)}
          >
            בחר מלון
          </button>
          <button
            type="button"
            className="hr-cvac-hcard__btn hr-cvac-hcard__btn--secondary"
            disabled={disabled || rank == null}
            onClick={() => rank != null && onViewPackageDetails?.(rank, card)}
          >
            פרטים על החבילה
          </button>
          {hasAbout ? (
            <button
              type="button"
              className="hr-cvac-hcard__btn hr-cvac-hcard__btn--secondary"
              disabled={disabled}
              onClick={() => setAboutOpen(true)}
            >
              אודות המלון
            </button>
          ) : null}
          {hasAlternates ? (
            <button
              type="button"
              className="hr-cvac-hcard__btn hr-cvac-hcard__btn--secondary"
              disabled={disabled}
              onClick={() => setAltOpen(true)}
            >
              אופציות נוספות
            </button>
          ) : null}
        </div>
      </div>

      <ChatSheet open={aboutOpen} title="אודות המלון" onClose={() => setAboutOpen(false)}>
        <p className="hr-cvac-sheet__text">{aboutFull}</p>
        {facilities.length ? (
          <ul className="hr-cvac-sheet__facilities">
            {facilities.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        ) : null}
      </ChatSheet>

      <ChatSheet open={altOpen} title="אופציות נוספות לאותו מלון" onClose={() => setAltOpen(false)}>
        <p className="hr-cvac-sheet__hint">אותו מלון — דילים שונים (מחיר / הרכב חבילה).</p>
        <div className="hr-cvac-hcard__alt-list">
          {alternates.map((alt) => {
            const id = String(alt?.lds_deal_id || "").trim();
            if (!id) return null;
            return (
              <button
                key={id}
                type="button"
                className="hr-cvac-hcard__alt-row"
                disabled={disabled || !onPickAlternateDeal}
                onClick={() => {
                  setAltOpen(false);
                  onPickAlternateDeal?.(id);
                }}
              >
                <span className="hr-cvac-hcard__alt-label">
                  {alt?.label_he || `דיל ${id}`}
                </span>
                <span className="hr-cvac-hcard__alt-chev">←</span>
              </button>
            );
          })}
        </div>
      </ChatSheet>
    </article>
  );
}
